import { validateApplicationFormValues } from './validators/validate-application-form-values';
import type { ApplicationValidationErrors } from './validators/validation-error-types';

import { ROUTE_IDS, RouteId, SectionName, sectionNames } from '../model';

const SECTION_ORDER = [
  sectionNames.ownership,
  sectionNames.businessInfo,
  sectionNames.registeredAddress,
  sectionNames.headOfficeAddress,
  sectionNames.businessDocuments,
  sectionNames.bankDetails,
  sectionNames.pointsOfSales,
  sectionNames.agents,
] as const;

export type ValidationRedirectEvent = {
  type: 'xstate.route';
  to: RouteId;
};

/**
 * Validates all form sections and returns the first invalid section in flow order, or null.
 */
export function getFirstInvalidSection(
  errors: ApplicationValidationErrors
): SectionName | null {
  for (const section of SECTION_ORDER) {
    const sectionErrors = errors[section];
    if (sectionErrors === undefined) continue;
    const hasErrors =
      (Array.isArray(sectionErrors) && sectionErrors.length > 0) ||
      (typeof sectionErrors === 'object' &&
        sectionErrors !== null &&
        Object.keys(sectionErrors).length > 0);
    if (hasErrors) return section;
  }
  return null;
}

/**
 * Runs full form validation and returns the first invalid section in flow order, or null.
 */
export function validateAndGetFirstInvalidSection(
  formValues: Partial<Record<SectionName, unknown>>,
  shouldSkipEmpty: boolean
): SectionName | null {
  const errors = validateApplicationFormValues(formValues, shouldSkipEmpty);
  return getFirstInvalidSection(errors);
}

const SECTION_TO_ROUTE_ID: Record<SectionName, RouteId> = {
  [sectionNames.ownership]: ROUTE_IDS.ownership,
  [sectionNames.businessInfo]: ROUTE_IDS.businessDetails,
  [sectionNames.registeredAddress]: ROUTE_IDS.businessAddress,
  [sectionNames.headOfficeAddress]: ROUTE_IDS.officeAddress,
  [sectionNames.businessDocuments]: ROUTE_IDS.businessDocuments,
  [sectionNames.bankDetails]: ROUTE_IDS.bankDetails,
  [sectionNames.pointsOfSales]: ROUTE_IDS.stores,
  [sectionNames.agents]: ROUTE_IDS.agents,
};

export function getRedirectEventForSection(
  section: SectionName
): ValidationRedirectEvent {
  return { type: 'xstate.route', to: SECTION_TO_ROUTE_ID[section] };
}

export const validationRedirectMessage = {
  title: 'Review your information',
  description:
    "Some details need updating. You'll be taken to the page where you can review and fix them.",
  confirmButtonText: 'Fix now',
};

/**
 * State value for the partner application machine to land on the given section's screen.
 * Used when resolving initial snapshot so the user opens on the first invalid section.
 */
export function getStateValueForSection(section: SectionName): {
  businessBlock?: string;
  storesBlock?: string;
  agentsBlock?: string;
} {
  const map: Record<
    SectionName,
    { businessBlock?: string; storesBlock?: string; agentsBlock?: string }
  > = {
    ownership: { businessBlock: 'ownership' },
    businessInfo: { businessBlock: 'businessDetails' },
    registeredAddress: { businessBlock: 'businessAddress' },
    headOfficeAddress: { businessBlock: 'officeAddress' },
    businessDocuments: { businessBlock: 'businessDocuments' },
    bankDetails: { businessBlock: 'bankDetails' },
    pointsOfSales: { storesBlock: 'storesList' },
    agents: { agentsBlock: 'agentsList' },
  };
  return map[section];
}
